import { useCallback } from 'react'
import { Separator } from '@/components/ui/separator'
import { Card, CardContent } from '@/components/ui/card'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'

import type {
  PaymentType,
  PaymentStatus,
  ShippingStatus,
  OrderPrivilegeStatus,
} from './order-privilege.types'

import {
  ORDER_STATUS_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
  SHIPPING_STATUS_OPTIONS,
  PAYMENT_TYPE_OPTIONS,
} from './helper'

export interface OrderPrivilegeFilters {
  page: number
  limit: number
  search?: string
  status?: OrderPrivilegeStatus
  shippingStatus?: ShippingStatus
  paymentStatus?: PaymentStatus
  paymentType?: PaymentType
  orderBy: string
}

interface OrderPrivilegeFilterProps {
  filters: OrderPrivilegeFilters
  onFiltersChange: (filters: Partial<OrderPrivilegeFilters>) => void
}

export default function OrderPrivilegeFilter({
  filters,
  onFiltersChange,
}: OrderPrivilegeFilterProps) {
  const handleStatusChange = useCallback(
    (value: string) => {
      onFiltersChange({
        status: value === 'all' ? undefined : (value as OrderPrivilegeStatus),
        page: 1,
      })
    },
    [onFiltersChange],
  )

  const handleShippingStatusChange = useCallback(
    (value: string) => {
      onFiltersChange({
        shippingStatus: value === 'all' ? undefined : (value as ShippingStatus),
        page: 1,
      })
    },
    [onFiltersChange],
  )

  const handlePaymentStatusChange = useCallback(
    (value: string) => {
      onFiltersChange({
        paymentStatus: value === 'all' ? undefined : (value as PaymentStatus),
        page: 1,
      })
    },
    [onFiltersChange],
  )

  const handlePaymentTypeChange = useCallback(
    (value: string) => {
      onFiltersChange({
        paymentType: value === 'all' ? undefined : (value as PaymentType),
        page: 1,
      })
    },
    [onFiltersChange],
  )

  const getValueOrAll = (value?: string) => (value ? value : 'all')

  return (
    <Card>
      <CardContent>
        <div className="flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-black">
              การจัดส่งของรางวัล
            </h2>
          </div>

          <Separator className="border-gray-200" />

          <div className="flex gap-6 flex-wrap">
            <div className="space-y-1">
              <p className="text-sm font-medium text-gray-500 mb-1">
                สถานะคำสั่ง
              </p>
              <Select
                value={getValueOrAll(filters.status)}
                onValueChange={handleStatusChange}
              >
                <SelectTrigger className="min-w-[180px]">
                  <SelectValue placeholder="เลือกสถานะคำสั่ง" />
                </SelectTrigger>
                <SelectContent>
                  {ORDER_STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={String(opt.value)}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <p className="text-sm font-medium text-gray-500 mb-1">
                สถานะการชำระเงิน
              </p>
              <Select
                value={getValueOrAll(filters.paymentStatus)}
                onValueChange={handlePaymentStatusChange}
              >
                <SelectTrigger className="min-w-[180px]">
                  <SelectValue placeholder="เลือกสถานะการชำระเงิน" />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={String(opt.value)}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <p className="text-sm font-medium text-gray-500 mb-1">
                สถานะการจัดส่ง
              </p>
              <Select
                value={getValueOrAll(filters.shippingStatus)}
                onValueChange={handleShippingStatusChange}
              >
                <SelectTrigger className="min-w-[180px]">
                  <SelectValue placeholder="เลือกสถานะการจัดส่ง" />
                </SelectTrigger>
                <SelectContent>
                  {SHIPPING_STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={String(opt.value)}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <p className="text-sm font-medium text-gray-500 mb-1">
                ประเภทการชำระเงิน
              </p>
              <Select
                value={getValueOrAll(filters.paymentType)}
                onValueChange={handlePaymentTypeChange}
              >
                <SelectTrigger className="min-w-[180px]">
                  <SelectValue placeholder="เลือกประเภทการชำระเงิน" />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={String(opt.value)}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
